import Seo from "../components/Seo";
import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import { ArrowLeft, Rocket } from "lucide-react";
type ThankYouLocationState = {
  childName?: string;
  children?: Array<{
    childName?: string;
  }>;
};

// ── helpers ────────────────────────────────────────────────────────────────

/** Join names naturally: "Ada" | "Ada and Bola" | "Ada, Bola and Chidi" */
function joinNames(names: string[]): string {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "child" for 1, "children" for any other number */
function childWord(count: number): string {
  return count === 1 ? "child" : "children";
}

// ── component ──────────────────────────────────────────────────────────────

export default function ThankYouPage() {
  const location = useLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = (location.state as ThankYouLocationState) || {};

  /** All full names, trimmed and non-empty */
  const childNames = useMemo(() => {
    if (Array.isArray(state.children) && state.children.length > 0) {
      return state.children
        .map((child) => child?.childName?.trim())
        .filter((name): name is string => !!name);
    }
    if (state.childName?.trim()) {
      return [state.childName.trim()];
    }
    return [];
  }, [state]);

  const count = childNames.length;
  const firstNames = childNames.map((name) => name.split(" ")[0]);

  // "Ada" | "Ada and Bola" | "Ada, Bola and Chidi"
  const joinedFirstNames = joinNames(firstNames);

  // "child" | "children"
  const word = childWord(count);

  /**
   * Headline:
   *   0  → "Your children are on the launchpad."
   *   1  → "Chidi is on the launchpad."
   *   2+ → "Ada and Bola are on the launchpad."
   *        "Ada, Bola and Chidi are on the launchpad."
   */
  const displayHeadline = useMemo(() => {
    if (count === 0) return "Your children are on the launchpad.";
    if (count === 1) return `${firstNames[0]} is on the launchpad.`;
    return `${joinedFirstNames} are on the launchpad.`;
  }, [count, firstNames, joinedFirstNames]);

  /**
   * Inline name reference for body copy:
   *   0  → "your children"
   *   1  → "Chidi"
   *   2+ → "Ada and Bola" | "Ada, Bola and Chidi"
   */
  const nameRef = useMemo(
    () => (count === 0 ? "your children" : joinedFirstNames),
    [count, joinedFirstNames],
  );

  /**
   * Step-04 sentence — verb and pronoun agree with count:
   *   1  → "Chidi takes the first exciting step…"
   *   2+ → "Ada and Bola take their first exciting steps…"
   */
  const codingText = useMemo(() => {
    if (count === 0) {
      return "Your children begin their first hands-on technology experience";
    }
    if (count === 1) {
      return `${firstNames[0]} takes the first exciting step into coding and digital creation`;
    }
    return `${joinedFirstNames} take their first exciting steps into coding and digital creation`;
  }, [count, firstNames, joinedFirstNames]);

  // ── confetti canvas ────────────────────────────────────────────────────
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ["#8b87d9", "#efb0a4", "#1b2d5e", "#ffffff", "#cdd4e2"];
    const pieces: {
      x: number;
      y: number;
      r: number;
      d: number;
      color: string;
      tilt: number;
      tiltAngle: number;
      tiltAngleInc: number;
    }[] = [];

    for (let i = 0; i < 120; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        r: Math.random() * 6 + 3,
        d: Math.random() * 80 + 20,
        color: colors[Math.floor(Math.random() * colors.length)],
        tilt: Math.floor(Math.random() * 10) - 10,
        tiltAngle: 0,
        tiltAngleInc: Math.random() * 0.07 + 0.05,
      });
    }

    let angle = 0;
    let animId: number;
    let running = true;

    function draw() {
      if (!ctx || !canvas || !running) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      angle += 0.01;

      pieces.forEach((p) => {
        p.tiltAngle += p.tiltAngleInc;
        p.y += (Math.cos(angle + p.d) + 2) * 0.8;
        p.x += Math.sin(angle) * 0.5;
        p.tilt = Math.sin(p.tiltAngle) * 12;

        ctx.beginPath();
        ctx.lineWidth = p.r / 2;
        ctx.strokeStyle = p.color;
        ctx.globalAlpha = 0.85;
        ctx.moveTo(p.x + p.tilt + p.r / 4, p.y);
        ctx.lineTo(p.x + p.tilt, p.y + p.tilt + p.r / 4);
        ctx.stroke();

        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
        }
      });

      animId = requestAnimationFrame(draw);
    }

    draw();

    const timeout = setTimeout(() => {
      running = false;
    }, 4000);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      running = false;
      cancelAnimationFrame(animId);
      clearTimeout(timeout);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ── render ────────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f5f4ef] px-5 py-8 text-[#16213e]">
      <Seo title="Welcome to the family" description="Your Future Smart Lab enrolment is confirmed." path="/thank-you" noindex />
      <canvas
        ref={canvasRef}
        className="pointer-events-none absolute inset-0 z-10"
      />

      <div className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#cdd4e2] blur-[120px]" />

      <div className="relative z-20 mx-auto max-w-5xl">
        <div className="overflow-hidden rounded-[36px] border border-[#dde2ec] bg-white shadow-[0_18px_50px_rgba(27,45,94,0.08)]">
          <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">

            {/* ── Left panel ── */}
            <div className="bg-[#1b2d5e] px-8 py-10 text-white md:px-10 md:py-12">
              <div className="flex h-full flex-col justify-between">
                <div>
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-4xl shadow-sm">
                    <Rocket className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                  </div>

                  <h1 className="mt-8 text-[3.2rem] font-semibold leading-[0.95] tracking-[-0.04em]">
                    {displayHeadline}
                  </h1>

                  <p className="mt-5 max-w-md text-base leading-7 text-white/85">
                    We&apos;ve received your registration for {nameRef}. Our
                    team will reach out within 24–48 hours with programme
                    details, start dates, and the next steps for{" "}
                    {count === 1 ? "your child" : "each child"}.
                  </p>
                </div>

                <div className="mt-10 rounded-[28px] bg-white/16 p-6 backdrop-blur-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">
                    Future Smart Lab
                  </p>
                  <p className="mt-3 text-sm leading-7 text-white/90">
                    &ldquo;The best time to prepare a child for the future is
                    before the future arrives.&rdquo;
                  </p>
                </div>
              </div>
            </div>

            {/* ── Right panel ── */}
            <div className="px-8 py-10 md:px-10 md:py-12">
              <div className="rounded-[28px] bg-[#f9fafc] p-6">
                <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-[#8189a0]">
                  What happens next
                </p>

                <div className="space-y-5">
                  {[
                    {
                      step: "01",
                      text: `We review the registration and confirm the right learning track for ${
                        count === 0
                          ? "each child"
                          : count === 1
                            ? firstNames[0]
                            : `each of your ${word}`
                      }`,
                    },
                    {
                      step: "02",
                      text: "You receive a welcome message with programme schedule, fees, and onboarding details",
                    },
                    {
                      step: "03",
                      text:
                        count === 0
                          ? `Your ${word} will be invited to a free trial session`
                          : count === 1
                            ? `${firstNames[0]} will be invited to a free trial session`
                            : `${joinedFirstNames} will each be invited to a free trial session`,
                    },
                    {
                      step: "04",
                      text: codingText,
                    },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#e8ecf5] text-xs font-semibold text-[#5b54c8]">
                        {item.step}
                      </span>
                      <p className="pt-1 text-sm leading-7 text-[#5a6278]">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Registered children chips */}
              {childNames.length > 0 && (
                <div className="mt-6 rounded-[24px] border border-[#dde2ec] bg-[#f9fafc] p-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#8189a0]">
                    Registered {word}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {childNames.map((name) => (
                      <span
                        key={name}
                        className="rounded-full border border-[#e5e8ef] bg-white px-4 py-2 text-sm font-medium text-[#5e5a78]"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/"
                  className="flex-1 rounded-full border border-[#dde2ec] bg-white py-4 text-center text-sm font-medium text-[#5a6278] transition hover:bg-[#f7f8fb]"
                >
                 {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back to Home
                </Link>
                <a
                  href="https://wa.me/2347052499758?text=Hi%20Future%20Smart%20Lab%2C%20I%20have%20a%20question%20about%20my%20child's%20enrollment."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 rounded-full bg-[#1b2d5e] py-4 text-center text-sm font-semibold text-white shadow-[0_12px_24px_rgba(27,45,94,0.28)] transition hover:bg-[#5b54c8]"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}